// ═══════════════════════════════════════════════════════════════════════════════
// src/App.jsx
// Router entry point. All navigation is now URL-based via react-router-dom.
// Shell chrome lives in Layout.jsx; this file just wires routes to views.
// ═══════════════════════════════════════════════════════════════════════════════

import { lazyRetry } from "./lib/chunkReload";
import UpdateAvailable from "./components/UpdateAvailable";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Loader } from "./components/ui";
import {
  usePracticePreferences,
} from "./hooks/usePracticePreferences";
import { useActiveAddons, skuMatches } from "./hooks/useActiveAddons";
import { useFeatureAccess } from "./hooks/useFeatureAccess";
import ProtectedRoute  from "./auth/ProtectedRoute";
import RoleRoute       from "./auth/RoleRoute";
import { useAuth }     from "./auth/AuthProvider";
import { legacyReportsTarget } from "./lib/insightsNav";
import { NAV_BY_ROLE } from "./lib/tokens";

import SuperAdminRoute from "./auth/SuperAdminRoute";
import ActivatePortal  from "./auth/ActivatePortal";
import SetPassword     from "./auth/SetPassword";
import LoginScreen     from "./auth/LoginScreen";
import Layout          from "./Layout";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

// Roles whose sidebar shows a tab; scheduling routes use the same list so a
// typed URL cannot reach a page the sidebar hides.
const rolesFor = (tab) => Object.keys(NAV_BY_ROLE).filter((r) => (NAV_BY_ROLE[r] || []).includes(tab));

// Every staff role (all roles with a nav list except Patient). For pages that
// are personal to the signed-in user rather than to a role, like /my-settings.
const STAFF_ROLES = Object.keys(NAV_BY_ROLE).filter((r) => r !== "Patient");

// Practice Settings (/settings): the roles whose sidebar shows Settings
// (Owner, Manager), plus Billing, which edits the Billing tab and is shown
// only that tab (SettingsView). Every other role is redirected like any
// RoleRoute. The wizard-backed sections (/settings?tab=identity etc., once
// /settings/practice/*) are practice configuration with Save buttons and no
// role check of their own, so they stay with the sidebar roles only: the
// Settings shell never lists them for Billing, and the old URLs keep this
// narrower RoleRoute.
const SETTINGS_ROLES = rolesFor("settings").concat(["Billing"]);

// Views -----------------------------------------------------------------------

// OrgConsoleView is lazy like every other view (below). It was the one eager
// import left, which put ~115 KB of network-admin code in every sign-in.

// ---------------------------------------------------------------------------
// Route-level code splitting.
//
// Every view is loaded on demand via React.lazy, so the entry bundle carries
// the shell (router, auth, layout) and each screen is fetched the first time a
// user navigates to it. Before this, all ~40 views shipped eagerly in a single
// 4 MB chunk that the login page had to download.
//
// Views are only ever rendered as <Route element>, so lazy loading changes
// nothing about behavior - just when the code arrives.
//
// SUSPENSE PLACEMENT MATTERS. Authenticated views suspend inside Layout
// (see the boundary around <Outlet> there), so the dark shell stays painted
// while a chunk loads. The boundary below exists only for the PUBLIC routes
// that render outside Layout (login, portal activation, set-password). It
// must NOT be the boundary for the authenticated tree - putting it there
// tore down the shell to a bare white page on every first navigation.
// ---------------------------------------------------------------------------
const DashboardView              = lazyRetry(() => import("./views/DashboardView"));
const OrgConsoleView             = lazyRetry(() => import("./features/org/OrgConsoleView"));
const ScheduleView               = lazyRetry(() => import("./views/ScheduleView"));
const CareScheduleView           = lazyRetry(() => import("./views/schedule/ScheduleView"));
const PatientsView               = lazyRetry(() => import("./views/PatientsView"));
const PatientChartPage           = lazyRetry(() => import("./views/patient/PatientChartPage"));
const QueueView                  = lazyRetry(() => import("./views/QueueView"));
const TasksView                  = lazyRetry(() => import("./views/TasksView"));
const ClinicalView               = lazyRetry(() => import("./views/ClinicalView"));
const InboxView                  = lazyRetry(() => import("./views/InboxView"));
const StaffView                  = lazyRetry(() => import("./views/StaffView"));
const MyTrainingView             = lazyRetry(() => import("./views/MyTrainingView"));
const MySettingsView             = lazyRetry(() => import("./views/MySettingsView"));
const ReportsView                = lazyRetry(() => import("./views/ReportsView"));
const SettingsView               = lazyRetry(() => import("./views/SettingsView"));
const BillingQueueView           = lazyRetry(() => import("./views/billing/BillingQueueView"));
const EligibilityView            = lazyRetry(() => import("./views/EligibilityView"));
const WaitlistView               = lazyRetry(() => import("./views/WaitlistView"));
const ReferralsAuthsView         = lazyRetry(() => import("./views/ReferralsAuthsView"));
const ComplianceView             = lazyRetry(() => import("./views/ComplianceView"));
const ConsentCapture             = lazyRetry(() => import("./views/ConsentCapture"));
const KioskApp                   = lazyRetry(() => import("./views/kiosk/KioskApp"));
const LobbyDisplayApp            = lazyRetry(() => import("./views/lobby/LobbyDisplayApp"));
const ConsentsView               = lazyRetry(() => import("./views/ConsentsView"));
const InsuranceUpdatesView       = lazyRetry(() => import("./views/InsuranceUpdatesView"));
const RecallView                 = lazyRetry(() => import("./views/RecallView"));
const CHWEngagementsView         = lazyRetry(() => import("./views/chw/CHWEngagementsView"));
const ChwMyPeoplePage            = lazyRetry(() => import("./views/chw/ChwMyPeoplePage"));
const CareManagementView         = lazyRetry(() => import("./views/CareManagementView"));
const VBPContractFormPage        = lazyRetry(() => import("./views/care-management/VBPContractFormPage"));
const VBPContractUploadPage      = lazyRetry(() => import("./views/care-management/VBPContractUploadPage"));
const VBPContractSummaryPage     = lazyRetry(() => import("./views/care-management/VBPContractSummaryPage"));
const CmCopilotView              = lazyRetry(() => import("./views/CmCopilotView"));
const SupervisorDashboardView    = lazyRetry(() => import("./views/care-management/SupervisorDashboardView"));
const SupervisorCmDetailView     = lazyRetry(() => import("./views/care-management/SupervisorCmDetailView"));
const MyScorecardView            = lazyRetry(() => import("./views/care-management/supervision/MyScorecardView"));
const AdministratorView          = lazyRetry(() => import("./views/admin/AdministratorView"));
const OnboardingWizard           = lazyRetry(() => import("./views/onboarding/OnboardingWizard"));
const ProAssistantView           = lazyRetry(() => import("./views/pro/AssistantView"));
const ProOutreachReviewView      = lazyRetry(() => import("./views/pro/OutreachReviewView"));
const ProInboundSMSReviewView    = lazyRetry(() => import("./views/pro/InboundSMSReviewView"));
const ProChartPrepView           = lazyRetry(() => import("./views/pro/ProChartPrepView"));
const ProHRSNView                = lazyRetry(() => import("./views/pro/ProHRSNView"));
const RecallCampaignsView        = lazyRetry(() => import("./views/RecallCampaignsView"));
const RecallBookingPage          = lazyRetry(() => import("./views/book/RecallBookingPage"));
const ExternalWorklistPage       = lazyRetry(() => import("./views/worklist/ExternalWorklistPage"));

// Command tier views (tier + role gate is inside each view)

// Super admin (Administrator) section

// Onboarding wizard (tier-agnostic, Owner+Manager only - gated inside view)

// Pro tier views (tier gate is inside each view)

// /settings/practice and /settings/practice/:section were a second Settings
// page (the wizard-backed sections). Oct 2026 they became sections of the one
// Settings shell, addressed as /settings?tab=<section>; this keeps bookmarks
// and old links working. It sits behind the same RoleRoute the old page had,
// so Billing (which reaches /settings for its Billing section only) is still
// sent to its dashboard rather than to its Billing section.
function PracticeSettingsRedirect() {
  const params = useParams();
  const section = (params["*"] || "").split("/")[0] || "identity";
  return <Navigate to={"/settings?tab=" + encodeURIComponent(section)} replace />;
}

// Tier 2 route guard. Mirrors the boolean checks used by Layout's sidebar
// filter so URL navigation to a disabled feature behaves like clicking a
// non-existent rail item: redirect to /dashboard. While prefs load we render
// nothing rather than flashing the gated view and snapping away.
function FeatureRoute({ requires, children }) {
  const { prefs, loaded } = usePracticePreferences();
  if (!loaded) return null;
  if (!requires(prefs)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Addon-based route guard. Mirrors FeatureRoute for SKU-gated features.
// Reads useActiveAddons() which RLS-scopes to the caller's practice.
// While addons load we render nothing rather than flashing the gated view
// and snapping away. Multiple SKUs can be required - all must be active.
function AddonRoute({ requires, children }) {
  const { skus, loading } = useActiveAddons();
  if (loading) return null;
  const required = Array.isArray(requires) ? requires : [requires];
  for (const sku of required) {
    // skuMatches, not skus.has - AddonRoute holds the raw Set and would
    // otherwise miss SKU aliases that the useActiveAddons().has() helper honours.
    if (!skuMatches(skus, sku)) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Capability-based route guard, backed by useFeatureAccess() - the SAME
// predicates Layout.jsx uses for the sidebar, so the rail and the router
// cannot disagree.
//
// Prefer this over FeatureRoute for anything tier- or role-sensitive.
// FeatureRoute checks ONLY practice preferences, so it let a Lite-tier user
// or a CHW reach a Pro view by typing the URL even though the sidebar hid
// it from them. This closes that path, matching the direct-URL guard
// CareManagementView already implements internally.
//
// `capability` is a key of the `can` map (e.g. "chartPrep").
function CapabilityRoute({ capability, children }) {
  const { ready, can } = useFeatureAccess();
  // Wait for prefs AND addons. Acting early makes every gate look false and
  // would bounce a legitimate user off a page they own.
  if (!ready) return null;
  const check = can[capability];
  // Unknown capability key is a programming error - fail CLOSED rather than
  // silently granting access to everyone.
  if (typeof check !== "function" || !check()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Reports and IC Insights are one "Insights" area at /insights (Oct 2026).
// Old /reports links (bookmarks, task links, cards) keep working: ?tab= is
// carried over, and a bare /reports opens the role's first report tab as it
// always did. Waits for the role (app_metadata, so it does not need the
// profile row) while auth is still loading, so the first tab is the role's.
function LegacyReportsRedirect() {
  const { role, loading } = useAuth();
  const { search } = useLocation();
  if (!role && loading) return null;
  return <Navigate to={legacyReportsTarget(search, role)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <UpdateAvailable />
      <RouteErrorBoundary>
      <Suspense fallback={<Loader />}>
      <Routes>
        {/* Public route: patient invite activation flow.
            Runs OUTSIDE ProtectedRoute so unauthenticated patients
            can land here from their invite email. */}
        <Route path="/activate" element={<ActivatePortal />} />

        {/* Public route: staff/owner invite + password recovery flow.
            Runs OUTSIDE ProtectedRoute since the user has a temp session
            from clicking an email link but hasn't set their password yet. */}
        <Route path="/set-password" element={<SetPassword />} />

        {/* Public route: forgot-password reset landing. Supabase redirects
            here after the user clicks the reset link in their email, with
            a recovery hash in the URL. LoginScreen detects recoveryMode
            and switches to the "set new password" view. Sits outside
            ProtectedRoute because Supabase's hash-established session is
            temporary and we don't want Layout/Dashboard rendering before
            the password is actually reset. */}
        <Route path="/reset-password" element={<LoginScreen recoveryMode />} />

        {/* Public route: token-driven consent capture page. Patient lands here
            from the email invite link sent by staff-send-consent-invite.
            Runs OUTSIDE ProtectedRoute since the patient is not logged in;
            the token IS the authentication. */}
        <Route path="/consent/:token" element={<ConsentCapture />} />

        {/* Public route: book a visit from a recall invite. The link token plus
            the patient's birth date are the authentication (recall_booking_link_*
            RPCs, granted to anon); no Supabase session is used or created. */}
        <Route path="/b/:token" element={<RecallBookingPage />} />

        {/* Public route: a network worklist for a practice not on PracticeOS.
            The link token plus a 6-digit access code are the authentication
            (cdl_worklist_external_* RPCs, granted to anon). */}
        <Route path="/worklist/:token" element={<ExternalWorklistPage />} />

        {/* Public route: patient self check-in kiosk. The device_token in
            localStorage (minted via Settings -> Kiosks by staff) IS the
            authentication; no Supabase JWT is used. Runs OUTSIDE
            ProtectedRoute so the kiosk surface never sees the auth shell. */}
        <Route path="/kiosk" element={<KioskApp />} />

        {/* Public route: the locked waiting room screen. A paired device token
            (Queue > Lobby display) IS the authentication; it never holds a
            staff session and has no way into the staff app. */}
        <Route path="/lobby-display" element={<LobbyDisplayApp />} />

        {/* All other routes require auth, and render inside the Layout shell. */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          {/* Default landing: redirect root to dashboard. */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard"         element={<DashboardView />} />
          <Route path="/schedule"          element={<RoleRoute allow={rolesFor("schedule")}><ScheduleView /></RoleRoute>} />
          <Route path="/care-schedule" element={
            <RoleRoute allow={["CHW","Care Manager","Supervising Care Manager","Care Manager Supervisor","Owner","Manager"]}>
              <CareScheduleView />
            </RoleRoute>
          } />
          <Route path="/patients"          element={<PatientsView />} />
          {/* Patient chart - splat catches all tab sub-routes (info, appts,
              notes, hedis, plan, etc). Tab list lives in PatientChartPage's
              VALID_TABS constant; adding a new tab does NOT require editing
              this file. Unknown sub-paths fall through to PatientChartPage's
              own urlTab fallback (defaults to "info") rather than bouncing
              the user to /dashboard. */}
          <Route path="/patients/:id/*" element={<PatientChartPage />} />
          <Route path="/queue"             element={<QueueView />} />
          <Route path="/tasks"             element={<TasksView />} />
          <Route path="/clinical"          element={<ClinicalView />} />
          <Route path="/inbox"             element={<InboxView />} />
          <Route path="/staff"             element={<StaffView />} />
          <Route path="/my-training"       element={<MyTrainingView />} />
          <Route path="/reports"           element={<LegacyReportsRedirect />} />
          <Route path="/settings"          element={<RoleRoute allow={SETTINGS_ROLES}><SettingsView /></RoleRoute>} />
          <Route path="/my-settings"       element={<RoleRoute allow={STAFF_ROLES}><MySettingsView /></RoleRoute>} />
          <Route path="/billing"           element={<BillingQueueView />} />
          <Route path="/eligibility"       element={<EligibilityView />} />
          <Route path="/waitlist"          element={<RoleRoute allow={rolesFor("waitlist")}><WaitlistView /></RoleRoute>} />
          <Route path="/referrals"         element={<RoleRoute allow={rolesFor("referrals")}><ReferralsAuthsView /></RoleRoute>} />
          {/* Insights home (views/ReportsView.jsx): Snapshot + every report tab. */}
          <Route path="/insights"          element={<ReportsView />} />
          <Route path="/compliance"        element={<ComplianceView />} />
          <Route path="/consents"          element={<ConsentsView />} />
          <Route path="/insurance-updates" element={<RoleRoute allow={rolesFor("insurance_updates")}><InsuranceUpdatesView /></RoleRoute>} />
          <Route path="/recall" element={<RoleRoute allow={rolesFor("recall")}><RecallView /></RoleRoute>} />
          <Route path="/campaigns" element={<RoleRoute allow={rolesFor("campaigns")}><RecallCampaignsView /></RoleRoute>} />
          {/* /portal is the patient portal's address. Layout renders PortalShell for
              the Patient role before this outlet, so only staff reach this route;
              the old staff-side portal page (PortalView) was unreachable for
              patients and is gone (round 6 bug 8). Old bookmarks land on the dashboard.
              Portal pages are /portal/<tab> (round 7): PortalShell reads the
              address itself (views/portal/portalRoutes.js), so no route is
              declared for them; for staff they fall to the catch-all below. */}
          <Route path="/portal"            element={<Navigate to="/dashboard" replace />} />
          <Route path="/chw-engagements"   element={
            <RoleRoute allow={["CHW","Care Manager","Supervising Care Manager","Care Manager Supervisor","Owner","Manager"]}>
              <CHWEngagementsView />
            </RoleRoute>
          } />
          <Route path="/my-people"         element={
            <RoleRoute allow={["CHW","Care Manager","Supervising Care Manager","Care Manager Supervisor","Owner","Manager"]}>
              <ChwMyPeoplePage />
            </RoleRoute>
          } />
          <Route path="/onboarding/*"      element={<OnboardingWizard />} />
          {/* Old address of the wizard-backed settings: now sections of /settings. */}
          <Route path="/settings/practice/*" element={<RoleRoute allow={rolesFor("settings")}><PracticeSettingsRedirect /></RoleRoute>} />

          {/* Network (MSO/CIN) console. Cosmetic nav gate lives in Layout;
              real enforcement is RLS on organizations + membership-gated org_kpi_rollup RPC. */}
          <Route path="/org" element={<OrgConsoleView />} />

          {/* Pro tier - each route also gated on the matching wizard pref */}
          <Route path="/pro/assistant" element={
            <CapabilityRoute capability="aiAssistant">
              <ProAssistantView />
            </CapabilityRoute>
          } />
          <Route path="/pro/outreach" element={
            <CapabilityRoute capability="outreach">
              <ProOutreachReviewView />
            </CapabilityRoute>
          } />
          <Route path="/pro/inbound-sms" element={
            <CapabilityRoute capability="inboundSms">
              <ProInboundSMSReviewView />
            </CapabilityRoute>
          } />
          <Route path="/pro/chart-prep" element={
            <CapabilityRoute capability="chartPrep">
              <ProChartPrepView />
            </CapabilityRoute>
          } />
          <Route path="/pro/hrsn" element={
            <CapabilityRoute capability="hrsn">
              <ProHRSNView />
            </CapabilityRoute>
          } />

          {/* Command tier - care management routes gated on delivery model */}
          <Route path="/care-management" element={
            <CapabilityRoute capability="careManagement">
              <CareManagementView />
            </CapabilityRoute>
          } />
          {/* VBP contract form is full-page (not a sub-tab); list view lives
              inside CareManagementView's VBP Contracts tab. /new must precede
              /:id so the literal route wins over the param route. The
              /:id/summary read-only view is the default landing page from
              the Quality Dashboard's "View contract" button - clinical staff
              should land on a summary, not the editor. Owners and Managers
              see an Edit button on the summary that takes them to the form.  */}
          <Route path="/care-management/vbp-contracts/upload" element={
            <RoleRoute allow={["Owner", "Manager"]}>
              <CapabilityRoute capability="careManagement">
                <VBPContractUploadPage />
              </CapabilityRoute>
            </RoleRoute>
          } />
          <Route path="/care-management/vbp-contracts/new" element={
            <CapabilityRoute capability="careManagement">
              <VBPContractFormPage />
            </CapabilityRoute>
          } />
          <Route path="/care-management/vbp-contracts/:id/summary" element={
            <CapabilityRoute capability="careManagement">
              <VBPContractSummaryPage />
            </CapabilityRoute>
          } />
          <Route path="/care-management/vbp-contracts/:id" element={
            <CapabilityRoute capability="careManagement">
              <VBPContractFormPage />
            </CapabilityRoute>
          } />

          {/* AMH Care Manager Co-pilot - addon-gated triage queue.
              Requires CM delivery (pref) AND the care_management_bundle
              SKU AND the amh_cm_copilot_addon SKU. Defense-in-depth:
              sidebar hides the nav item without the addons (cosmetic),
              AddonRoute redirects URL access (this line), and RLS on
              cm_copilot_tasks blocks data reads if both miss. */}
          <Route path="/care-management/copilot" element={
            <CapabilityRoute capability="careManagement">
              <AddonRoute requires={["care_management_bundle", "amh_cm_copilot_addon"]}>
                <CmCopilotView />
              </AddonRoute>
            </CapabilityRoute>
          } />

          {/* CM Supervisor Dashboard - team oversight (caseload, workload,
              VBP rollup, panel signals). CM delivery model gates the feature;
              RoleRoute restricts to supervisor roles. Defense-in-depth: the
              get_cm_supervisor_dashboard RPC also enforces supervisor role +
              practice scope server-side, and reads are practice-scoped. */}
          <Route path="/care-management/supervisor" element={
            <CapabilityRoute capability="careManagement">
              <RoleRoute allow={["Owner","Manager","Supervising Care Manager","Care Manager Supervisor"]}>
                <SupervisorDashboardView />
              </RoleRoute>
            </CapabilityRoute>
          } />
          <Route path="/care-management/supervisor/cm/:userId" element={
            <CapabilityRoute capability="careManagement">
              <RoleRoute allow={["Owner","Manager","Supervising Care Manager","Care Manager Supervisor"]}>
                <SupervisorCmDetailView />
              </RoleRoute>
            </CapabilityRoute>
          } />

          {/* A care manager's own caseload quality scorecard. The RPC scopes
              a Care Manager to their own card server-side. */}
          <Route path="/care-management/my-scorecard" element={
            <CapabilityRoute capability="careManagement">
              <RoleRoute allow={["Care Manager","Supervising Care Manager","Care Manager Supervisor","Owner","Manager"]}>
                <MyScorecardView />
              </RoleRoute>
            </CapabilityRoute>
          } />

          {/* Every console section gets a real URL, so the browser back button
              works and a section can be linked or bookmarked. Declared after
              the specific /care-management/* routes above: React Router v6
              ranks static segments above dynamic ones, so /copilot and
              /supervisor still win over :tab, but declaring it last means a
              reader does not have to know that to trust the file. */}
          <Route path="/care-management/:tab" element={
            <CapabilityRoute capability="careManagement">
              <CareManagementView />
            </CapabilityRoute>
          } />

          {/* Super admin section. Splat catches sub-tabs (subscriptions, practices, health, flags, audit, settings).
              SuperAdminRoute redirects non-super-admins to /dashboard before AdministratorView mounts.
              Defense-in-depth: rail item is conditional (Layout), route is gated (here), and DB has RLS. */}
          <Route path="/admin/*" element={<SuperAdminRoute><AdministratorView /></SuperAdminRoute>} />

          {/* Any unknown protected URL falls back to dashboard. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </BrowserRouter>
  );
}
